import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { saveAs } from 'file-saver';

import { cameraStreamManager } from 'services/CameraStream/CameraStreamManager';
import { formControlActions } from 'store/formControlSlice/formControlSlice';
import { getNowFormattedDate } from 'utils/getNowFormatedDate';

import TakeSnapshot from 'components/UI/TakeSnapshot/TakeSnapshot';
import TakeAllSnapshots from 'components/UI/TakeAllSnapshots/TakeAllSnapshots';
import { IMAGE_CATEGORIES } from '../constants';

const downloadImageDataUrl = (dataUrl, filename) => {
    const match = dataUrl?.match(/^data:image\/(\w+); ?base64,(.*)$/);
    if (!match) return;

    const ext = match[1] === 'jpeg' ? 'jpg' : match[1];
    const base64Data = match[2];
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: `image/${ext}` });
    saveAs(blob, `${filename}.${ext}`);
};

// A snapshot id is also its key in the media upload, which an OCR read waits on: two snapshots taken in the same
// millisecond (an OCR read next to a regular one) must still differ. Time-based, so ordering by id is kept.
let lastSnapshotId = 0;
export const nextSnapshotId = () => {
    lastSnapshotId = Math.max(Date.now(), lastSnapshotId + 1);
    return lastSnapshotId;
};

const useTakeSnapshot = ({
    transactionId,
    streamingCameraIds,
    tabId,
    camInfo = {},
    rotation = null,
    title = 'All cameras',
    isDisabledSnapshot = false,
    expandedCamVideoId = '',
}) => {
    const dispatch = useDispatch();
    const [snapshots, setSnaphots] = useState([]);
    const videoElementId = expandedCamVideoId ? expandedCamVideoId : camInfo?.camVideoId;

    const createOnSnapshotHandler = useCallback(
        ({ onComplete, onSaved, downloadImmediately, namePrefix }) =>
            function onSnapshotHandler(_, base64) {
                const snapshot = base64 ? base64.split(',').at(1) : null;
                const imageName = `${namePrefix ? `${namePrefix} ` : ''}${title} ${getNowFormattedDate()}`;
                const id = nextSnapshotId();
                const cameraName = camInfo?.camConfig?.name || this || null;
                dispatch(
                    formControlActions.saveCameraStreamSnapshot({
                        transactionId,
                        snapshot,
                        id,
                        imageCategory: IMAGE_CATEGORIES.CAMERA_SNAPSHOT,
                        imageName,
                        cameraName,
                    })
                );

                // the id is the snapshot's key in the media upload, which an OCR read waits on
                if (typeof onSaved === 'function') onSaved({ snapshotId: id, cameraName, captured: !!snapshot });

                if (downloadImmediately && typeof base64 === 'string') {
                    downloadImageDataUrl(base64, imageName);
                }

                if (onComplete && typeof onComplete === 'function') {
                    onComplete();
                }
            },
        [dispatch, transactionId, title, camInfo?.camConfig?.name]
    );

    const onTakeSnapshot = useCallback(
        (isSnapshotAllModeOrOptions = false, onComplete = null) => {
            let isSnapshotAllMode = false;
            let completeCallback = onComplete;
            let downloadImmediately = false;
            let onSaved = null;
            let namePrefix = null;

            if (typeof isSnapshotAllModeOrOptions === 'object' && isSnapshotAllModeOrOptions !== null) {
                isSnapshotAllMode = isSnapshotAllModeOrOptions.isSnapshotAll || false;
                completeCallback = isSnapshotAllModeOrOptions.onComplete || null;
                downloadImmediately = isSnapshotAllModeOrOptions.downloadImmediately || false;
                onSaved = isSnapshotAllModeOrOptions.onSaved || null;
                namePrefix = isSnapshotAllModeOrOptions.namePrefix || null;
            } else if (typeof isSnapshotAllModeOrOptions === 'boolean') {
                isSnapshotAllMode = isSnapshotAllModeOrOptions;
            }

            if (isDisabledSnapshot || streamingCameraIds?.length === 0) {
                if (completeCallback) completeCallback();
                return;
            }

            const id = Date.now();
            const snapshotHandler = createOnSnapshotHandler({ onComplete: completeCallback, onSaved, downloadImmediately, namePrefix });

            const snapshot =
                isSnapshotAllMode && streamingCameraIds?.length > 1 ? (
                    <TakeAllSnapshots
                        key={id}
                        id={id}
                        videoElementIds={streamingCameraIds.map((camId) => cameraStreamManager.getVideoElementId(tabId, camId))}
                        onSnapshot={snapshotHandler.bind('All Cameras')}
                    />
                ) : (
                    <TakeSnapshot key={id} id={id} videoElementId={videoElementId} onSnapshot={snapshotHandler} rotation={rotation} />
                );
            setSnaphots([snapshot]);
        },
        [isDisabledSnapshot, streamingCameraIds, createOnSnapshotHandler, videoElementId, rotation, tabId]
    );

    return { onTakeSnapshot, snapshots };
};

export default useTakeSnapshot;
